import React from "react";
import { Wallet, FileDown } from "lucide-react";
import { SectionFrame } from "./SectionFrame";
import { StatusBadge } from "../../StatusBadge";
import { payrollApi } from "../../../services/payrollApi";

/** Monthly Record - Payroll + Payslip sections (spec items 5-6). The wage profile shown is
 * the one payroll_service.get_effective_wage_profile resolved as effective on the LAST day
 * of this period - never the employee's current wage if it changed since. A payslip is
 * only ever shown for a payroll_line already PUBLISHED - never implied from a draft
 * calculation. */
export const PayrollSection = ({ data, loading, error, onRetry }) => {
  const line = data?.payroll_line;
  const wage = data?.wage_profile;
  return (
    <SectionFrame
      title="Payroll" icon={Wallet} testId="monthly-payroll-section"
      loading={loading} error={error} onRetry={onRetry}
      isEmpty={!line && !wage}
      emptyMessage="Payroll has not yet been calculated for this period."
    >
      {wage && (
        <dl className="text-sm grid grid-cols-2 gap-2 mb-4" data-testid="monthly-payroll-wage-profile">
          <div className="flex justify-between col-span-2 sm:col-span-1"><dt className="text-slate-500">Effective wage</dt>
            <dd>¥{Number(wage.hourly_rate).toLocaleString()}{wage.compensation_type === "FIXED_ASSIGNMENT" ? " / assignment" : " / hr"}</dd></div>
          <div className="flex justify-between col-span-2 sm:col-span-1"><dt className="text-slate-500">Effective from</dt><dd>{wage.effective_from}</dd></div>
        </dl>
      )}

      {line ? (
        <>
          <div className="flex items-center justify-between mb-3">
            <StatusBadge status={line.status} />
            <p className="text-lg font-bold text-slate-900" data-testid="monthly-payroll-gross">¥{Number(line.gross_pay || 0).toLocaleString()}</p>
          </div>
          <dl className="text-sm space-y-1.5 mb-4">
            <div className="flex justify-between"><dt className="text-slate-500">Regular hours</dt><dd>{line.regular_hours ?? "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Late-night hours</dt><dd>{line.late_night_hours ?? "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Base pay</dt><dd>¥{Number(line.regular_pay || 0).toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Qualifying shift premium</dt><dd data-testid="monthly-payroll-premium">¥{Number(line.premium_pay || 0).toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Transportation allowance</dt><dd>¥{Number(line.transportation_allowance || 0).toLocaleString()}</dd></div>
            {(line.blockers || []).length > 0 && (
              <div className="text-red-600 text-xs" data-testid="monthly-payroll-blockers">Blocked: {line.blockers.join("; ")}</div>
            )}
          </dl>

          <div className="pt-3 border-t border-slate-100">
            <p className="text-[11px] text-slate-400 uppercase mb-2">Payslip</p>
            {data?.payslip_available ? (
              <a
                data-testid="monthly-payslip-download-link"
                href={payrollApi.getLineReportUrl(line.id, "detailed")}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-2 w-fit px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 active:scale-95 transition-all"
              >
                <FileDown size={14} /> Download published payslip
              </a>
            ) : (
              <p className="text-sm text-slate-500" data-testid="monthly-payslip-not-available">No published payslip yet - payroll must be published first.</p>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-500" data-testid="monthly-payroll-line-pending">Payroll has not yet been calculated for this period.</p>
      )}
    </SectionFrame>
  );
};
